import {
  TextInput,
  ConfirmModal,
  AutoCompleteInput,
  CustomButton,
  CustomSelect,
  PhoneInput,
} from '@/shared/ui/components';
import { phoneFormat, checkToFormatPhoneNumber } from '@/shared/lib';
import { t } from 'ttag';
import { getContent } from '@/entities/study';
import { GOOGLE_API_KEY } from '@/shared/config';
import { useContactDrawer } from './useContactDrawer';
import type { ContactDrawerProps } from './ContactDrawer.types';
import './style.scss';

/**
 * Renders the patient contact drawer with country, address, city, state, zip, email and two phone fields bound to the useContactDrawer state. Country selection drives the address/city autocompletes and swaps the state field between a divisions dropdown and a free-text input when the country formats phone numbers and divisions are loaded, while a confirmation modal gates applying the changes and the save button reflects the saving flag.
 *
 * @param data - Existing patient contact values used to seed the form.
 * @param title - Drawer section title passed through to the drawer hook.
 * @param handleClick - Callback closing the drawer, also wired to the cancel button.
 * @param isSaving - Whether a save request is in flight, disabling cancel and loading save.
 * @param countries - Country options for the select and for the phone number country pickers.
 * @returns The contact editing form with confirmation modal and action buttons.
 */
const ContactDrawer = ({ data, title, handleClick, isSaving, countries }: ContactDrawerProps) => {
  const {
    state,
    setState,
    isEmailRequired,
    handleChange,
    onChangeSelection,
    onChangeInputAutoComplete,
    onChangeAutoComplete,
    clearAutoComplete,
    onClickSave,
    onClickBottomButton,
  } = useContactDrawer({ data, title, handleClick });

  return (
    <>
      <ConfirmModal
        title={t`Apply changes`}
        question={getContent()}
        isOpen={state.isOpenConfirmModal}
        leftButtonName={t`Cancel`}
        rightButtonName={t`Apply changes`}
        onClickLeftButton={() => {
          setState({
            isOpenConfirmModal: false,
          });
        }}
        onClickRightButton={() => {
          onClickBottomButton();
        }}
        isSaving={isSaving}
      />
      <div className="list-input">
        <CustomSelect
          title={t`Country`}
          aria-label={t`Country`}
          showSearch
          allowClear={false}
          isError={!!state.errorObject.country}
          errorMessage={state.errorObject.country}
          name="country"
          size="middle"
          options={countries.map((country) => ({
            ...country,
            title: country.label,
          }))}
          value={state.country}
          onChange={onChangeSelection}
        />
        <AutoCompleteInput
          googleApiKey={GOOGLE_API_KEY}
          allowClear
          size="small"
          isSearchCity={false}
          title={t`Address`}
          placeholder={t`Enter address`}
          isError={!!state.errorObject.address}
          country={state.country}
          errorMessage={state.errorObject.address}
          onChangeInput={onChangeInputAutoComplete}
          onChangeAddress={onChangeAutoComplete}
          eventClear={clearAutoComplete}
          disabled={state.isLoadingDivisions}
          value={state.address}
        />
        <div className="inline-input">
          <AutoCompleteInput
            googleApiKey={GOOGLE_API_KEY}
            className="city-input"
            size="small"
            isSearchCity
            id="city"
            title={t`City`}
            placeholder={t`Enter city`}
            country={state.country}
            value={state.city}
            onChangeInput={onChangeInputAutoComplete}
            onChangeAddress={onChangeAutoComplete}
            eventClear={clearAutoComplete}
            disabled={state.isLoadingDivisions}
            isError={!!state.errorObject.city}
            errorMessage={state.errorObject.city}
          />
          {checkToFormatPhoneNumber(state.country) && state.divisions ? (
            <CustomSelect
              title={t`State`}
              aria-label={t`State`}
              className="state-input"
              showSearch
              allowClear={false}
              isError={!!state.errorObject.state}
              errorMessage={state.errorObject.state}
              name="state"
              id="state"
              size="middle"
              placeholder={state.isLoadingDivisions ? t`Loading...` : t`Select a state`}
              options={state.divisions?.map((division: any) => ({
                ...division,
                title: division.label,
              }))}
              value={state.state}
              onChange={onChangeSelection}
            />
          ) : (
            <TextInput
              className="state-input"
              name="state"
              label={t`State`}
              placeholder={t`Enter state`}
              value={state.state}
              onChange={handleChange}
              isError={!!state.errorObject.state}
              errorMessage={state.errorObject.state}
            />
          )}
        </div>
        <TextInput
          name="zip"
          label={t`Zip code`}
          placeholder={t`Enter zip code`}
          value={state.zip}
          onChange={handleChange}
          disabled={state.isLoadingDivisions}
          isError={!!state.errorObject.zip}
          errorMessage={state.errorObject.zip}
        />

        <PhoneInput
          title={t`Primary phone number`}
          name="phone1"
          countryValue={state.country}
          value={phoneFormat(state.phone1, state.country)}
          placeholder="800-555-1234"
          onChange={handleChange}
          size="small"
          isError={!!state.errorObject.phone1}
          errorMessage={state.errorObject.phone1}
          maxLength={15}
          countryOptions={countries}
        />
        <TextInput
          name="email"
          label={
            <div>
              {t`Email`}{' '}
              <span className="optional-label">({isEmailRequired ? 'required' : 'optional'})</span>
            </div>
          }
          placeholder="johnsmith@mail.com"
          value={state.email}
          onChange={handleChange}
          isError={!!state.errorObject.email}
          errorMessage={state.errorObject.email}
        />
        <PhoneInput
          title={
            <div>
              {t`Secondary phone number`} <span className="optional-label">(optional)</span>
            </div>
          }
          name="phone2"
          countryValue={state.country}
          value={phoneFormat(state.phone2, state.country)}
          placeholder="800-555-1234"
          onChange={handleChange}
          size="small"
          isError={!!state.errorObject.phone2}
          errorMessage={state.errorObject.phone2}
          maxLength={15}
          countryOptions={countries}
        />
        <div className="bottom-button-row">
          <CustomButton
            id="cancel-btn"
            style="neutral"
            type="outline"
            onClick={handleClick}
            styles={{ minWidth: 80 }}
            disabled={isSaving}
          >
            {t`Cancel`}
          </CustomButton>
          <CustomButton
            id="save-btn"
            onClick={onClickSave}
            styles={{ minWidth: 80 }}
            loading={isSaving}
          >
            {t`Save`}
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default ContactDrawer;
